import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FileUpload } from './FileUpload';
import { useFileUpload } from '../hooks/useFileUpload';
import { Message } from '../types';

// Интерфейс для пропсов компонента MessageInput
export interface MessageInputProps {
  onSendMessage: (content: string, files?: File[]) => Promise<boolean | undefined> | void;
  disabled?: boolean;
  isUploading?: boolean;
  placeholder?: string;
  className?: string;
  showFileButton?: boolean; // Показывать ли кнопку прикрепления файлов
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  isUploading = false,
  placeholder = 'Введите сообщение...',
  className = '',
  showFileButton = true // По умолчанию показываем кнопку
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const {
    selectedFiles,
    handleFileSelect,
    handleDrop,
    handlePaste,
    removeFile,
    clearFiles
  } = useFileUpload();

  /**
   * Автоматическое изменение высоты textarea
   */
  const autoResize = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, []);

  /**
   * Обработка изменения текста
   */
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    autoResize();
  };

  /**
   * Обработка нажатия клавиш
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Отправка сообщения
   */
  const handleSend = async () => {
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage && selectedFiles.length === 0) {
      return;
    }

    if (disabled) {
      return;
    }

    // Отправляем сообщение
    const result = await onSendMessage(
      trimmedMessage, 
      selectedFiles.length > 0 ? selectedFiles : undefined
    );
    
    // Очищаем поля ТОЛЬКО если сообщение успешно отправлено
    // Если result === false (ошибка) или undefined (нет получателя), НЕ очищаем
    if (result === true) {
      setMessage('');
      clearFiles();
      

      
      // Сбрасываем высоту textarea ТОЛЬКО при успешной отправке
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  /**
   * Обработка вставки из буфера обмена
   */
  const handleTextareaPaste = (e: React.ClipboardEvent) => {
    // Обрабатываем файлы из буфера обмена
    handlePaste(e.nativeEvent);
  };

  /**
   * Открытие диалога выбора файлов
   */
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };



  return (
    <div ref={containerRef} className={`chat-widget-input-container ${className}`}>
      
      {/* Превью выбранных файлов */}
      {selectedFiles.length > 0 && (
        <div className="relative">
          <FileUpload
            files={selectedFiles}
            onRemoveFile={removeFile}
            onClearAll={clearFiles}
          />
          {/* Оверлей загрузки */}
          {isUploading && (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="loading-spinner" style={{ width: '20px', height: '20px' }}></div>
                <span className="text-sm font-medium">Загрузка файлов...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Поле ввода */}
      <div className="chat-widget-input-wrapper">
        
        {/* Кнопка прикрепления файлов - показываем только если разрешено */}
        {showFileButton && (
          <button
            type="button"
            onClick={openFileDialog}
            disabled={disabled}
            className="chat-widget-file-btn"
            title="Прикрепить файл"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>
        )}

        {/* Скрытый input для файлов */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="chat-widget-file-input"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
        />

        {/* Группа ввода */}
        <div className="chat-widget-input-group">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            onPaste={handleTextareaPaste}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className="chat-widget-input"
          />

          {/* Кнопка отправки */}
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || (!message.trim() && selectedFiles.length === 0) || isUploading}
            className="chat-widget-send"
            title={isUploading ? "Загрузка файлов..." : "Отправить сообщение"}
          >
            {isUploading ? (
              <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
            ) : (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
